import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Microphone, Stop, Spinner, PaperPlaneTilt, Check, X, Pause, Play,
  Phone, WhatsappLogo, EnvelopeSimple, Calendar, ListChecks, WarningCircle,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { isNative, nativeSpeechStart, nativeHaptic, nativeHapticNotification } from "@/lib/native";

// iter 275 hotfix: queste costanti erano usate dal preview di estrazione
// (PRIORITY_COLORS, CHANNEL_ICON, fmtEur, ListChecks) ma dichiarate solo
// in Agenda.jsx come module-globals. Quando il MemoRecorder veniva aperto
// dal GlobalMicFab su una pagina diversa da Agenda (es. /dashboard/stima)
// React crashava con "Can't find variable: CHANNEL_ICON". Le dichiariamo
// localmente qui, così il componente è autonomo. */
const PRIORITY_COLORS = {
  urgente: "bg-red-100 text-red-700 border-red-200",
  alta: "bg-amber-100 text-amber-800 border-amber-200",
  media: "bg-[#EDEAE2] text-[#7C8B6E] border-[#E8E5DE]",
  bassa: "bg-[#FAFAF7] text-[#9A968B] border-[#E8E5DE]",
};
const CHANNEL_ICON = {
  chiamata: Phone,
  whatsapp: WhatsappLogo,
  email: EnvelopeSimple,
  incontro: Calendar,
  interno: ListChecks,
};
const fmtEur = (n) => n ? "€ " + Math.round(n).toLocaleString("it-IT") : "—";

// iter v33 r108 · FIX B · Rimosso _looksLikeQuestion(): il routing locale
// scavalcava /assistant/text-route causando full page reload + doppia chiamata
// LLM. Ora ogni testo passa dal router backend (che ha già il fast-path chat).

/**
 * Memo recorder modal — voice (Whisper) or text, with live AI extraction
 * preview + save. Extracted from Agenda.jsx (iter 100 refactor).
 *
 * Iter 234: aggiunto pause/resume nativo (mediaRecorder.pause/resume).
 * Durante la pausa il timer si ferma ma la sessione registrazione resta
 * aperta — premi resume e riprendi senza chiudere il file audio.
 */
export default function MemoRecorder({ open, onClose, onCreated, routeMode = "agenda", autoStart = false }) {
  // iter r96c · Bug fix "m is not a function": alcuni chiamanti (BottomTabBar
  // smart mic, GlobalMicFab) invocano MemoRecorder senza passare onCreated.
  // Al momento del "Conferma e salva" il codice faceva onCreated(data) →
  // undefined(data) → crash minified "m is not a function".
  // Fallback no-op: nessun refresh dati padre (che non è nemmeno registrato),
  // ma il salvataggio backend va comunque a buon fine e l'utente vede
  // l'evento in Agenda al prossimo mount.
  const notifyCreated = typeof onCreated === "function" ? onCreated : () => {};
  // routeMode:
  //   "agenda"    → comportamento storico: trascrive + estrae entità agenda → preview
  //   "assistant" → smart routing: trascrive + classifica intent → se "agenda" mostra
  //                 stessa preview, se "chat" naviga all'Assistente con la query
  const navigate = useNavigate();
  const [mode, setMode] = useState("voice"); // voice | text
  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [rec, setRec] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [preview, setPreview] = useState(null);
  // iter v29 r168 — App Store: live transcript via native speech (iOS only)
  // gira in parallelo a MediaRecorder. Backend Whisper resta il source-of-truth
  // per l'estrazione, ma l'utente vede le parole comparire mentre parla.
  const [livePartial, setLivePartial] = useState("");
  // iter v31 r17 — Diagnostica voce (visibile in fondo al modal). Aiuta a
  // capire se il device sta usando SFSpeechRecognizer nativo o il fallback
  // web MediaRecorder + Whisper. Soprattutto utile su Capacitor iOS dove il
  // plugin SpeechRecognition può fallire silenziosamente.
  const [voiceMode, setVoiceMode] = useState(null); // "native" | "web" | "web-fallback" | null
  const [voiceError, setVoiceError] = useState(null);
  const nativeSpeechRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setMode("voice"); setText(""); setPreview(null);
      setIsRecording(false); setIsPaused(false); setElapsed(0);
      setLivePartial("");
      if (rec && rec.state !== "inactive") try { rec.stop(); } catch { /* */ }
      try { nativeSpeechRef.current?.stop?.(); } catch (_) { /* */ }
      nativeSpeechRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [open, rec]);

  // iter v31 r9 — Auto-start della registrazione quando il modal si apre
  // con autoStart=true (cioè dal mic centrale della bottom tab bar).
  // L'utente diceva: «se no devi cliccare due volte, non si capisce».
  // Con un solo tap parte subito la registrazione, niente schermata intermedia.
  useEffect(() => {
    if (open && autoStart && !isRecording && !processing) {
      // micro-delay così la modal ha tempo di renderizzare prima del prompt
      // permessi microfono iOS (che altrimenti compare prima dell'animazione)
      const t = setTimeout(() => { start(); }, 120);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStart]);

  const start = async () => {
    try {
      // iter v29 r168 — Haptic "start record" su iOS: l'utente sente il
      // dispositivo confermare il tap prima ancora che parta l'animazione.
      nativeHaptic("medium").catch(() => {});

      // iter v31 r17 — Strategia: su iOS proviamo prima SpeechRecognition
      // nativa (on-device, no upload). Se fallisce (plugin n/a, permessi
      // negati, device non supportato), fallback automatico a MediaRecorder
      // + Whisper come sul web. L'utente NON deve mai vedere errori legati
      // al microfono.
      if (isNative) {
        const nativeOk = await tryNativeSpeech();
        if (nativeOk) return;
        // Native failed → continua sotto col fallback web (MediaRecorder)
        // Salviamo la diagnostica per il pannello "Voce" in fondo al modal
        setVoiceMode("web-fallback");
      } else {
        setVoiceMode("web");
      }

      // Web (Safari/Chrome/Firefox) OR fallback iOS: MediaRecorder + Whisper
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
      const mime = candidates.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) clearInterval(timerRef.current);
        setIsRecording(false); setIsPaused(false);
        if (chunks.length === 0) return;
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        await upload(blob);
      };
      mr.start();
      setRec(mr);
      setIsRecording(true);
      setIsPaused(false);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((t) => t + 1), 1000);
    } catch (e) {
      toast.error(e?.message || "Microfono non accessibile");
    }
  };

  // iter v31 r17 — Prova SpeechRecognition nativa iOS. Ritorna true se OK
  // (l'utente sta parlando, vediamo livePartial popolarsi), false se ha
  // fallito in modo recuperabile (plugin n/a, permessi negati, device
  // unsupported). Il chiamante usa il return per decidere se fare il
  // fallback web. NESSUN toast d'errore qui: la diagnostica va in
  // voiceMode + voiceError per essere visibile in modo discreto.
  const tryNativeSpeech = async () => {
    setLivePartial("");
    setIsRecording(true);
    setIsPaused(false);
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((t) => t + 1), 1000);
    try {
      const handle = await nativeSpeechStart({
        language: "it-IT",
        partial: true,
        onPartial: (txt) => setLivePartial(txt || ""),
        onError: (err) => {
          // onError viene chiamato a runtime dopo che start ha già funzionato.
          // A questo punto non possiamo fare fallback (microfono già usato),
          // quindi mostriamo l'errore ma in modo non panico.
          setVoiceError(err?.message || "Errore voce");
          if (timerRef.current) clearInterval(timerRef.current);
          setIsRecording(false);
          setLivePartial("");
        },
      });
      if (!handle) {
        // start() ha ritornato null senza throw → permessi negati o n/a
        if (timerRef.current) clearInterval(timerRef.current);
        setIsRecording(false);
        return false;
      }
      nativeSpeechRef.current = handle;
      setVoiceMode("native");
      setVoiceError(null);
      return true;
    } catch (e) {
      // Qualsiasi errore: import del plugin fallito, available()=false,
      // requestPermissions denied, start() crashato. Cleanup e ritorna
      // false così il chiamante fa fallback a MediaRecorder.
      if (timerRef.current) clearInterval(timerRef.current);
      setIsRecording(false);
      setLivePartial("");
      setVoiceError(`Native fail: ${e?.message || String(e)}`);
      return false;
    }
  };

  const togglePause = () => {
    if (!rec) return;
    if (rec.state === "recording") {
      rec.pause();
      setIsPaused(true);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    } else if (rec.state === "paused") {
      rec.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => setElapsed((t) => t + 1), 1000);
    }
  };

  const stop = async () => {
    // iter v29 r168 — Haptic light "stop": conferma tattile fine recording
    nativeHaptic("light").catch(() => {});

    // iter v31 r17 — Stop dipende dal voiceMode attivo (impostato in start):
    // - "native"        → speech recognition iOS, prendiamo livePartial e text-route
    // - "web-fallback"  → MediaRecorder iOS partito dopo che native è fallito
    // - "web"           → MediaRecorder browser
    // I 2 path web vanno entrambi via rec.stop() + upload(blob).
    if (voiceMode === "native") {
      try { nativeSpeechRef.current?.stop?.(); } catch (_) { /* ignore */ }
      nativeSpeechRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      setIsRecording(false); setIsPaused(false);
      const finalText = (livePartial || "").trim();
      if (!finalText || finalText.length < 4) {
        toast.error("Non ho sentito niente. Riprova parlando più vicino al microfono.");
        return;
      }
      await uploadText(finalText);
      return;
    }

    if (rec?.state !== "inactive") rec?.stop();
    try { nativeSpeechRef.current?.stop?.(); } catch (_) { /* ignore */ }
    nativeSpeechRef.current = null;
  };

  // iter v31 r10 — Haptic semantico dopo l'estrazione: "ho capito quello che
  // hai detto" lo senti senza guardare lo schermo. Success (pattern alto)
  // se l'estrattore ha trovato contatti o azioni concrete; Warning altrimenti.
  // iter v31 r13 — BUGFIX user vedeva toast rosso "m is not a function" al
  // ritorno della preview: in produzione il binding ESM minificato di
  // nativeHapticNotification poteva risultare undefined in alcuni edge case
  // (webpack chunk loading, hot reload). Wrappiamo TUTTE le chiamate haptic
  // in safeHaptic() che e' indistruttibile: typeof guard + try/catch + .catch
  // su Promise. Nessun haptic deve mai bloccare il flow di salvataggio.
  const safeHaptic = (type) => {
    try {
      if (typeof nativeHapticNotification !== "function") return;
      const p = nativeHapticNotification(type);
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch (_) { /* silent */ }
  };

  const triggerExtractionHaptic = (data) => {
    const hasContacts = (data?.extracted?.contacts?.length || 0) > 0;
    const hasActions = (data?.extracted?.actions?.length || 0) > 0;
    safeHaptic(hasContacts || hasActions ? "success" : "warning");
  };

  // iter v33 r74 · Helper: naviga all'Assistente. Se il backend ha
  // gia' generato la risposta (prefetch in voice-route/text-route),
  // la passiamo via location.state cosi' Assistente.jsx la mostra
  // subito senza chiamare di nuovo /assistant/ask. -5/8s di attesa.
  const _goToAssistant = (data) => {
    onClose();
    if (data?.answer && data?.conversation_id) {
      navigate("/dashboard/assistente", {
        state: {
          prefetched: {
            conversation_id: data.conversation_id,
            user_message: data.user_message,
            answer: data.answer,
          },
        },
      });
    } else {
      // Fallback: percorso storico con ?q=<domanda> → Assistente ri-chiamera' /ask
      const q = encodeURIComponent(data?.transcript || "");
      window.location.href = `/dashboard/assistente?q=${q}`;
    }
  };

  // iter v30 r135 — Smart Auto-Routing del microfono globale.
  // routeMode="assistant" → il backend classifica intent e (per chat)
  // pre-genera la risposta. routeMode="agenda" → flow storico agenda.
  const uploadText = async (transcriptText) => {
    setProcessing(true);
    try {
      if (routeMode === "assistant") {
        const { data } = await api.post("/assistant/text-route", { text: transcriptText }, { timeout: 60000 });
        if (data?.intent === "chat") {
          _goToAssistant(data);
          return;
        }
        triggerExtractionHaptic(data);
        setPreview({ ...data, _saved: false });
        return;
      }
      const { data } = await api.post("/agenda/preview", { text: transcriptText }, { timeout: 60000 });
      triggerExtractionHaptic(data);
      setPreview({ ...data, _saved: false });
    } catch (e) {
      safeHaptic("error");
      toast.error(apiError(e, "Errore analisi"));
    } finally {
      setProcessing(false);
    }
  };

  const upload = async (blob) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm";
      fd.append("file", blob, `memo.${ext}`);
      // iter v30 r135 — Smart routing per BottomTabBar mic:
      //   routeMode="assistant" → /assistant/voice-route: il backend trascrive,
      //     classifica l'intent (agenda vs chat) e ritorna il payload giusto.
      //   routeMode="agenda" (default) → /agenda/voice?preview=1: trascrive +
      //     estrae direttamente come anteprima agenda (comportamento storico).
      const endpoint = routeMode === "assistant"
        ? "/assistant/voice-route"
        : "/agenda/voice?preview=1";
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });

      // Se siamo in modalità assistant e l'intent è "chat", non mostriamo
      // la preview agenda: chiudiamo la modal e mandiamo l'utente alla
      // pagina Assistente. Se il backend ha gia' generato la risposta
      // (prefetch), la passiamo via state cosi' viene mostrata subito.
      if (routeMode === "assistant" && data?.intent === "chat") {
        _goToAssistant(data);
        return;
      }

      // Altrimenti (intent agenda, o routeMode agenda): mostra preview agenda
      triggerExtractionHaptic(data);
      setPreview({ ...data, _saved: false });
    } catch (e) {
      safeHaptic("error");
      // iter v31 r17 — Su 500 (Whisper non riesce a trascrivere l'audio iOS
      // WebKit registrato dal MediaRecorder), suggeriamo all'utente di usare
      // il tab "Scrivi" invece di mostrare il misterioso "Request failed
      // with status code 500". L'iPhone genera audio mp4/aac in formato a
      // volte rifiutato dall'API Whisper.
      const status = e?.response?.status;
      if (status === 500) {
        toast.error("Voce non disponibile · usa il tab Scrivi", { duration: 5000 });
        setMode("text");
      } else {
        toast.error(apiError(e, "Errore registrazione"));
      }
    } finally {
      setProcessing(false);
    }
  };

  const submitText = async () => {
    const t = text.trim();
    if (t.length < 5) { toast.error("Scrivi almeno qualche parola"); return; }
    setProcessing(true);
    try {
      // iter v33 r108 · FIX B · UN solo router lato server.
      // Rimosso _looksLikeQuestion() locale: ogni testo passa da
      // /assistant/text-route che ha già il fast-path chat, il classifier
      // LLM e il prefetch della risposta. Niente più window.location.href
      // (full page reload). Niente più bypass del router.
      if (routeMode === "assistant") {
        const { data } = await api.post("/assistant/text-route", { text: t }, { timeout: 60000 });
        if (data?.intent === "chat") {
          _goToAssistant(data); // SPA navigate + state.prefetched (già impl)
          return;
        }
        triggerExtractionHaptic(data);
        setPreview({ ...data, _saved: false });
        return;
      }
      const { data } = await api.post("/agenda/preview", { text: t }, { timeout: 60000 });
      triggerExtractionHaptic(data);
      setPreview({ ...data, _saved: false });
    } catch (e) {
      safeHaptic("error");
      toast.error(apiError(e, "Errore analisi"));
    } finally {
      setProcessing(false);
    }
  };

  const confirm = async () => {
    // Both voice + text paths now go through /agenda/apply.
    // Includiamo summary + transcript modificati dall'utente (se l'AI ha
    // sentito un nome sbagliato, qui la correzione è già recepita).
    setProcessing(true);
    try {
      const merged = preview.extracted ? { ...preview.extracted, summary: preview.summary } : preview.extracted;
      const { data } = await api.post("/agenda/apply", {
        transcript: preview.transcript,
        extracted: merged,
      });
      safeHaptic("success");
      notifyCreated(data);
      onClose();
    } catch (e) {
      safeHaptic("error");
      toast.error(apiError(e, "Errore salvataggio"));
    } finally {
      setProcessing(false);
    }
  };

  const discard = () => {
    // Nothing was saved yet — just close the preview.
    setPreview(null);
    toast.success("Memo scartato");
    onClose();
  };

  if (!open) return null;
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end lg:items-center justify-center lg:p-6" data-testid="memo-modal">
      {/* iter 275: full-height su mobile + header sticky, allineato a ManualEventModal */}
      <div className="w-full lg:max-w-xl bg-white rounded-t-2xl lg:rounded-2xl shadow-2xl overflow-hidden flex flex-col h-[92dvh] lg:h-auto lg:max-h-[92vh]">
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-[#E8E5DE] bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">
              {routeMode === "assistant" ? "Assistente" : "Nuovo memo"}
            </div>
            <div className="text-[15px] font-semibold text-[#1C1C1A]">
              {routeMode === "assistant" ? "Parla all\u2019Assistente" : "Parla con Pronto"}
            </div>
            {routeMode === "assistant" && (
              <div className="text-[11px] text-[#7C8B6E] mt-0.5">
                Capisco anche promemoria e appuntamenti
              </div>
            )}
            {/* iter v31 r17 — Diagnostica voce: visibile solo se stiamo
                registrando, in piccolissimo e discreto, per capire quale
                path stiamo usando senza disturbare l'utente medio. */}
            {(voiceMode || voiceError) && isRecording && (
              <div className="text-[10px] text-[#A8A89F] mt-1 font-mono" data-testid="voice-mode-diag">
                {voiceMode === "native" && "voce: nativa iOS"}
                {voiceMode === "web" && "voce: web"}
                {voiceMode === "web-fallback" && "voce: fallback web (native n/a)"}
                {voiceError && ` · ${voiceError.slice(0, 60)}`}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white border border-[#E5E7E0] hover:border-[#1C1C1A] hover:bg-[#F1F4ED] flex items-center justify-center text-[#1C1C1A] shadow-sm"
            data-testid="memo-close"
            aria-label="Chiudi"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {!preview ? (
          <>
            <div className="flex border-b border-[#E8E5DE] text-[12px]">
              <button onClick={() => setMode("voice")} className={`flex-1 py-2.5 font-medium transition-colors ${mode === "voice" ? "text-[#1C1C1A] border-b-2 border-[#7C8B6E]" : "text-[#9A968B]"}`} data-testid="memo-tab-voice">Voce</button>
              <button onClick={() => setMode("text")} className={`flex-1 py-2.5 font-medium transition-colors ${mode === "text" ? "text-[#1C1C1A] border-b-2 border-[#7C8B6E]" : "text-[#9A968B]"}`} data-testid="memo-tab-text">Scrivi</button>
            </div>

            {mode === "voice" && (
              <div className="p-6 text-center" data-testid="memo-voice-panel">
                {processing ? (
                  <div className="py-6">
                    <Spinner size={32} className="animate-spin mx-auto text-[#7C8B6E]" />
                    <div className="mt-3 text-[13px] text-[#6B6A65]">Trascrivo e analizzo…</div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={isRecording ? stop : start}
                      className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto transition-all ${
                        isRecording
                          ? (isPaused
                              ? "bg-[#7C8B6E] hover:bg-[#6B7A5D] shadow-lg"
                              : "bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/40 animate-pulse")
                          : "bg-[#7C8B6E] hover:bg-[#6B7A5D]"
                      }`}
                      data-testid="memo-record-btn"
                      aria-label={isRecording ? "Termina registrazione" : "Inizia registrazione"}
                    >
                      {isRecording ? <Stop size={32} weight="fill" color="#fff" /> : <Microphone size={32} weight="fill" color="#fff" />}
                    </button>
                    {isRecording && !isNative && (
                      <button
                        onClick={togglePause}
                        className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-[#E8E5DE] text-[12.5px] font-semibold text-[#1C1C1A] hover:border-[#7C8B6E] transition-colors"
                        data-testid="memo-pause-btn"
                      >
                        {isPaused
                          ? <><Play size={14} weight="fill" className="text-[#7C8B6E]" /> Riprendi</>
                          : <><Pause size={14} weight="fill" className="text-[#7C8B6E]" /> Pausa</>}
                      </button>
                    )}
                    <div className="mt-4 text-[13px] text-[#6B6A65]">
                      {isRecording ? (
                        <span className="font-mono text-[15px] text-[#1C1C1A]">
                          {mm}:{ss}{isPaused && <span className="ml-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[#7C8B6E]">· in pausa</span>}
                        </span>
                      ) : (
                        "Premi e parla — raccontami clienti, immobili, cosa devi fare."
                      )}
                    </div>

                    {/* iter v29 r168 — Live transcript native iOS.
                        Mostra le parole on-device mentre l'utente parla. Il
                        Whisper backend resta source-of-truth (audio uploadato
                        anche), ma l'agente vede subito che il sistema sente
                        e capisce. Su web/Android resta vuoto. */}
                    {isNative && isRecording && livePartial && (
                      <div className="mt-3 text-left bg-[#F1F4ED] border border-[#7C8B6E]/25 rounded-lg px-3 py-2.5" data-testid="memo-native-partial">
                        <div className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-[#5A6A4D] mb-1">Sento…</div>
                        <div className="text-[13px] text-[#1C1C1A] leading-snug">{livePartial}</div>
                      </div>
                    )}

                    {/* iter v29 r073 — Quick-suggest tap-and-go.
                        User M1437: «forse non è chiaro che il pulsante "Parla
                        con Pronto" sia anche per agenda vocale». Mostriamo
                        3 esempi tappabili che esplicitano i casi d'uso:
                        agenda, sopralluogo, nota rapida. Tocca un esempio →
                        finisce nel campo testo già scritto. */}
                    {!isRecording && (
                      <div className="mt-5 space-y-2" data-testid="memo-quick-suggest">
                        <div className="text-[10px] uppercase tracking-[0.20em] font-semibold text-[#9A968B] text-center mb-3">
                          o tocca un esempio
                        </div>
                        {[
                          { ic: Calendar,   label: "Agenda visita",      text: "Domani alle 15 visita in Via Brera con i Bianchi, trilocale 500k." },
                          { ic: ListChecks, label: "Promemoria",         text: "Ricordami giovedì alle 10 di mandare la proposta a Andrea Bianchi." },
                          { ic: Phone,      label: "Domanda immobiliare", text: "Quanto rende mediamente un bilocale in zona Brera affittato a studenti?" },
                        ].map((sug, i) => {
                          const Ic = sug.ic;
                          return (
                            <button
                              key={i}
                              onClick={() => { setMode("text"); setText(sug.text); }}
                              className="w-full flex items-start gap-3 p-3 bg-[#FAFAF7] hover:bg-[#F1F4ED] border border-[#E8E5DE] hover:border-[#7C8B6E] rounded-md text-left transition-colors group"
                              data-testid={`memo-quick-suggest-${i}`}
                            >
                              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-[#E8E5DE] group-hover:border-[#7C8B6E] flex items-center justify-center transition-colors">
                                <Ic size={14} weight="fill" className="text-[#7C8B6E]" />
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-[10px] uppercase tracking-[0.16em] font-semibold text-[#7C8B6E]">{sug.label}</span>
                                <span className="block text-[12.5px] text-[#1C1C1A] leading-snug mt-0.5">{sug.text}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {mode === "text" && (
              <div className="p-5" data-testid="memo-text-panel">
                <textarea
                  rows={5}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Esempio: Oggi ho visto la signora Ferrari in Isola, budget 350k, vuole il box…"
                  className="w-full px-3 py-3 border border-[#E8E5DE] rounded-md text-[14px] leading-[1.55] focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="memo-text-input"
                />
                <button
                  onClick={submitText}
                  disabled={processing || text.trim().length < 5}
                  className="mt-3 w-full py-3 bg-[#1C1C1A] hover:bg-black disabled:opacity-50 text-white font-medium rounded-md flex items-center justify-center gap-2 transition-colors"
                  data-testid="memo-text-submit"
                >
                  {processing ? <><Spinner size={15} className="animate-spin" /> Analizzo…</> : <><PaperPlaneTilt size={15} weight="fill" /> Analizza</>}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto" data-testid="memo-preview">
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9A968B]">Sintesi · modificabile</div>
                <div className="text-[9.5px] text-[#9A968B]">{(preview.summary || "").length}/280</div>
              </div>
              <input
                type="text"
                value={preview.summary || ""}
                onChange={(e) => setPreview({ ...preview, summary: e.target.value.slice(0, 280) })}
                className="w-full px-3 py-2 text-[14px] text-[#1C1C1A] font-medium border border-[#E8E5DE] rounded-md focus:outline-none focus:border-[#7C8B6E]"
                data-testid="memo-summary"
                placeholder="Una frase riassuntiva"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1">Trascrizione · modificabile</div>
              <textarea
                rows={4}
                value={preview.transcript || ""}
                onChange={(e) => setPreview({ ...preview, transcript: e.target.value })}
                className="w-full px-3 py-2 text-[13px] text-[#1C1C1A] leading-[1.55] bg-[#FAFAF7] border border-[#E8E5DE] rounded-md focus:outline-none focus:border-[#7C8B6E] resize-y"
                data-testid="memo-transcript"
                placeholder="Testo del memo"
              />
              <div className="mt-1 text-[10.5px] text-[#9A968B] italic">
                Correggi qui prima di salvare (es. nomi sbagliati). Contatti e azioni vengono comunque salvate; potrai modificarle anche dopo.
              </div>
            </div>
            {preview.extracted?.contacts?.length === 0 && preview.extracted?.actions?.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-[12.5px] text-amber-900" data-testid="memo-vague">
                <div className="font-semibold mb-0.5">Memo vago</div>
                <div className="text-[11.5px]">Non ho trovato contatti o azioni concrete. Se confermi, salvo solo il testo in timeline. Se vuoi più precisione, riformula dicendo ad esempio: "Ricordare di richiamare Marco Bianchi entro domani".</div>
              </div>
            )}
            {preview.extracted?.contacts?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">Contatti rilevati · {preview.extracted.contacts.length}</div>
                <div className="space-y-2">
                  {preview.extracted.contacts.map((c, i) => (
                    <div key={i} className="border border-[#E8E5DE] rounded-md p-2.5 text-[12.5px]" data-testid={`memo-contact-${i}`}>
                      <div className="font-semibold text-[#1C1C1A]">{c.name} <span className="text-[10px] uppercase text-[#9A968B] font-normal">· {c.role}</span></div>
                      {(c.budget_max || c.zones?.length || c.must_have?.length) && (
                        <div className="text-[11.5px] text-[#6B6A65] mt-0.5">
                          {c.budget_max && <>budget {fmtEur(c.budget_max)} · </>}
                          {c.zones?.length > 0 && <>{c.zones.join(", ")} · </>}
                          {c.must_have?.length > 0 && <>must-have: {c.must_have.join(", ")}</>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {preview.extracted?.actions?.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-1.5">Azioni create · {preview.extracted.actions.length}</div>
                {/* iter v32 r16 — Banner di avviso se ci sono azioni senza
                    data. Prima si creavano todo con date inventate (+3gg)
                    senza dirlo. Ora l'agente vede chiaramente il problema
                    e può scrivere "quando" inline per ogni azione. */}
                {(() => {
                  const missing = preview.extracted.actions.filter((a) => !a.due || !String(a.due).trim());
                  if (missing.length === 0) return null;
                  return (
                    <div
                      className="mb-2 flex items-start gap-2 px-2.5 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-[12px]"
                      data-testid="memo-missing-date-banner"
                    >
                      <WarningCircle size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                      <div className="leading-snug">
                        <div className="font-semibold">
                          {missing.length === 1 ? "Manca il quando" : `Mancano ${missing.length} date`}
                        </div>
                        <div className="text-[11.5px]">
                          Per ogni azione qui sotto puoi scrivere &laquo;quando&raquo; (es. <em>domani alle 10</em>, <em>venerdì mattina</em>) o lasciarla senza data.
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div className="space-y-1.5">
                  {preview.extracted.actions.map((a, i) => {
                    const CIcon = CHANNEL_ICON[a.channel] || ListChecks;
                    const hasDate = !!(a.due && String(a.due).trim());
                    return (
                      <div key={i} className="flex flex-col gap-1 text-[12.5px]" data-testid={`memo-action-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-[9px] uppercase tracking-[0.1em] font-bold px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[a.priority] || PRIORITY_COLORS.media}`}>{a.priority}</span>
                          <CIcon size={12} className="text-[#7C8B6E]" />
                          <span className="flex-1">{a.what}</span>
                          {hasDate && (
                            <span className="text-[11px] text-[#9A968B]" data-testid={`memo-action-${i}-due`}>{a.due}</span>
                          )}
                        </div>
                        {!hasDate && (
                          <input
                            type="text"
                            value={a.due || ""}
                            onChange={(e) => {
                              const next = [...preview.extracted.actions];
                              next[i] = { ...next[i], due: e.target.value };
                              setPreview({
                                ...preview,
                                extracted: { ...preview.extracted, actions: next },
                              });
                            }}
                            placeholder="Quando? (es. domani alle 10)"
                            className="ml-7 w-[calc(100%-1.75rem)] px-2 py-1 border border-amber-300 bg-amber-50/40 rounded text-[12px] placeholder-amber-700/60 focus:outline-none focus:border-amber-500 focus:bg-white"
                            data-testid={`memo-action-${i}-due-input`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 pt-2">
              <button onClick={discard} className="flex-1 py-2 border border-[#E8E5DE] rounded-md text-[13px] text-[#9A968B] hover:bg-[#FAFAF7] transition-colors" data-testid="memo-discard">
                Scarta
              </button>
              <button onClick={confirm} disabled={processing} className="flex-1 py-2 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white font-medium rounded-md text-[13px] flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50" data-testid="memo-confirm">
                {processing ? <Spinner size={13} className="animate-spin" /> : <Check size={13} weight="bold" />}
                Conferma e salva
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
